import { OPTIBOARD_ARCHIVE_WINDOW } from '@/utils/optiBoardTabPredicates';
import { useState, useEffect, useMemo, useRef } from 'react';
// Core-100 R4 X4 (CORE-AS Rel): the board says when live updates are paused, and re-reads when they resume.
import { LiveUpdatesBanner, nextLiveState, type LiveState } from '@/components/dashboard/LiveUpdatesBanner';
import { isAbnormalRealtimeClose } from '@/utils/realtimeStatusLog';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { useOptiboardTickets } from '@/hooks/useOptiboardTickets';
import { useTicketCustodyRealtime } from '@/hooks/tickets/useTicketCustodyRealtime';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { OptiboardTicketRow } from '@/components/optiboard/OptiboardTicketRow';
import { format, parseISO, isToday } from 'date-fns';
import { getTodayInShopTz } from '@/utils/shopTime';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, FileText, Calendar, Play, CheckCircle, Truck, Trash2, Search, LayoutGrid } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { filterOptiBoardTickets } from '@/lib/search/filterOptiBoardTickets';
import { usePermissions } from '@/hooks/usePermissions';

type TabValue = 'all' | 'estimates' | 'scheduled' | 'wip' | 'completed' | 'picked-up' | 'deleted';

// CORE-AS: search matches the columns the board shows (see filterOptiBoardTickets).
const filterTickets = filterOptiBoardTickets;

const OptiBoard = () => {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  // Core-100 R4 (OD-17): the Deleted tab is for managers and owners (the server returns no deleted rows to anyone else).
  const { canViewDeletedTickets } = usePermissions();

  // Lifecycle v2 — slice 4A.2. Keep OptiboardTicketRow custody chips
  // live when another user changes custody on this shop. No-op when
  // the flag is off; see useTicketCustodyRealtime.
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  useTicketCustodyRealtime(activeShop?.id ?? null, custodyHandoffEnabled);

  // Get initial tab from URL or default to estimates
  const requestedTab = (searchParams.get('tab') as TabValue) || 'estimates';
  const initialTab: TabValue = requestedTab === 'deleted' && !canViewDeletedTickets ? 'estimates' : requestedTab;
  const [currentTab, setCurrentTab] = useState<TabValue>(initialTab);

  // Fetch tickets
  const { 
    estimates, 
    scheduled, 
    wip, 
    completed, 
    pickedUp, 
    deleted,
    technicians,
    isLoading,
    archiveTruncated,
    liveMayBeTruncated,
    refetch 
  } = useOptiboardTickets(activeShop?.id || '', shopTimezone);

  // Shop-day "today", for marking overdue scheduled work. Overdue work stays in
  // Scheduled (the lifecycle stage is the canonical production state, never a
  // date cutoff); the badge only says its day has passed.
  const shopToday = getTodayInShopTz(shopTimezone);

  // Group scheduled tickets by date
  const scheduledByDate = useMemo(() => {
    const groups: Record<string, typeof scheduled> = {};
    scheduled.forEach(ticket => {
      const date = ticket.scheduled_date || 'Unscheduled';
      if (!groups[date]) groups[date] = [];
      groups[date].push(ticket);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [scheduled]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    const tabValue = value as TabValue;
    setCurrentTab(tabValue);
    setSearchParams({ tab: tabValue });
  };

  const [liveState, setLiveState] = useState<LiveState>({ live: true });
  const liveLostRef = useRef(false);

  // Real-time subscription for ticket changes
  useEffect(() => {
    if (!activeShop?.id) return;

    // Chronomancer R-RT-4: scope channel name with shop id so two tabs from
    // different shops don't collide on the same bare `optiboard-tickets` name.
    const channel = supabase
      .channel(`optiboard-tickets-${activeShop.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tickets',
          filter: `shop_id=eq.${activeShop.id}`,
        },
        (payload) => {
          // Chronomancer R-RT-5: defence-in-depth shop_id guard mirroring
          // Birdseye/ShopFloor/useLiveReshuffle. Server-side filter is
          // authoritative; this is a no-regrets safety net.
          const row = (payload.new || payload.old) as any;
          if (row?.shop_id && row.shop_id !== activeShop.id) return;
          refetch();
        }
      )
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: surface channel drops so a stale OptiBoard
        // is diagnosable. This is the primary scheduling surface — a
        // silent TIMED_OUT here leaves the board stuck until a refresh.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[OptiBoard] realtime channel status:', status, err);
        }
        // Core-100 R4 X4 (CORE-AS Rel): a lost channel is SAID (the board may be out of date since that moment);
        // the rejoin re-reads the board, because changes made while it was down are not replayed.
        const abnormal = isAbnormalRealtimeClose(status, err);
        setLiveState((prev) => nextLiveState(prev, String(status), abnormal, new Date()));
        if (abnormal) liveLostRef.current = true;
        if (String(status) === 'SUBSCRIBED' && liveLostRef.current) {
          liveLostRef.current = false;
          refetch();
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeShop?.id, refetch]);

  const isSearchActive = searchQuery.length >= 2;

  // Filter all ticket arrays based on search query
  const filteredEstimates = useMemo(() => filterTickets(estimates, searchQuery), [estimates, searchQuery]);
  const filteredScheduled = useMemo(() => filterTickets(scheduled, searchQuery), [scheduled, searchQuery]);
  const filteredWip = useMemo(() => filterTickets(wip, searchQuery), [wip, searchQuery]);
  const filteredCompleted = useMemo(() => filterTickets(completed, searchQuery), [completed, searchQuery]);
  const filteredPickedUp = useMemo(() => filterTickets(pickedUp, searchQuery), [pickedUp, searchQuery]);
  const filteredDeleted = useMemo(() => filterTickets(deleted, searchQuery), [deleted, searchQuery]);

  // Combine all filtered tickets with their tab info for cross-tab search results
  const allSearchResults = useMemo(() => {
    if (!isSearchActive) return [];
    return [
      ...filteredEstimates.map(t => ({ ...t, _tab: 'estimates' as TabValue })),
      ...filteredScheduled.map(t => ({ ...t, _tab: 'scheduled' as TabValue })),
      ...filteredWip.map(t => ({ ...t, _tab: 'wip' as TabValue })),
      ...filteredCompleted.map(t => ({ ...t, _tab: 'completed' as TabValue })),
      ...filteredPickedUp.map(t => ({ ...t, _tab: 'picked-up' as TabValue })),
      ...filteredDeleted.map(t => ({ ...t, _tab: 'deleted' as TabValue })),
    ];
  }, [isSearchActive, filteredEstimates, filteredScheduled, filteredWip, filteredCompleted, filteredPickedUp, filteredDeleted]);

  const getTabCount = (tab: TabValue): number => {
    switch (tab) {
      case 'all': return estimates.length + scheduled.length + wip.length + completed.length;
      case 'estimates': return estimates.length;
      case 'scheduled': return scheduled.length;
      case 'wip': return wip.length;
      case 'completed': return completed.length;
      case 'picked-up': return pickedUp.length;
      case 'deleted': return deleted.length;
      default: return 0;
    }
  };

  const getTabLabel = (tab: TabValue): string => {
    switch (tab) {
      case 'all': return 'All';
      case 'estimates': return 'Estimate';
      case 'scheduled': return 'Scheduled';
      case 'wip': return 'WIP';
      case 'completed': return 'Completed';
      case 'picked-up': return 'Picked Up';
      case 'deleted': return 'Deleted';
      default: return '';
    }
  };

  const allTabs: { value: TabValue; label: string; icon: React.ReactNode; color: string; activeClass: string }[] = [
    { value: 'all', label: 'All', icon: <LayoutGrid className="h-4 w-4" />, color: 'text-muted-foreground', activeClass: 'data-[state=active]:bg-background' },
    { value: 'estimates', label: 'Estimates', icon: <FileText className="h-4 w-4" />, color: 'text-blue-500', activeClass: 'data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-600 data-[state=active]:border-b-2 data-[state=active]:border-blue-500' },
    { value: 'scheduled', label: 'Scheduled', icon: <Calendar className="h-4 w-4" />, color: 'text-violet-500', activeClass: 'data-[state=active]:bg-violet-500/10 data-[state=active]:text-violet-600 data-[state=active]:border-b-2 data-[state=active]:border-violet-500' },
    { value: 'wip', label: 'WIP', icon: <Play className="h-4 w-4" />, color: 'text-amber-500', activeClass: 'data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-600 data-[state=active]:border-b-2 data-[state=active]:border-amber-500' },
    { value: 'completed', label: 'Completed', icon: <CheckCircle className="h-4 w-4" />, color: 'text-green-500', activeClass: 'data-[state=active]:bg-green-500/10 data-[state=active]:text-green-600 data-[state=active]:border-b-2 data-[state=active]:border-green-500' },
    { value: 'picked-up', label: 'Picked Up', icon: <Truck className="h-4 w-4" />, color: 'text-teal-500', activeClass: 'data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-600 data-[state=active]:border-b-2 data-[state=active]:border-teal-500' },
    { value: 'deleted', label: 'Deleted', icon: <Trash2 className="h-4 w-4" />, color: 'text-rose-500', activeClass: 'data-[state=active]:bg-rose-500/10 data-[state=active]:text-rose-600 data-[state=active]:border-b-2 data-[state=active]:border-rose-500' },
  ];
  const tabConfig = allTabs.filter((t) => t.value !== 'deleted' || canViewDeletedTickets);

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  // Table header component for reuse - adds Status column for search results
  const TableHeader = ({ showStatus = false, tab }: { showStatus?: boolean; tab?: TabValue }) => {
    const useParkingSpot = tab === 'scheduled' || tab === 'completed' || tab === 'picked-up';
    const usePickedUpTime = tab === 'picked-up';
    return (
      <thead>
        <tr>
          <th colSpan={showStatus ? 12 : 11} className="px-0 pb-2">
            <div className="flex items-center bg-muted/50 rounded-lg">
              {showStatus && <div className="px-4 py-3 text-left text-sm font-medium min-w-[100px]">Status</div>}
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[120px]">Ticket #</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[160px]">Customer</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[180px]">Vehicle</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[180px] flex-1">Jobs</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[120px]">Technician</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[100px]">{useParkingSpot ? 'Parking Spot' : 'Bay'}</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[80px]">{usePickedUpTime ? 'Picked Up' : 'Start Time'}</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[100px]">Scheduled</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[90px]">Timer</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[90px]">Book Time</div>
              <div className="px-4 py-3 text-left text-sm font-medium min-w-[160px]">Actions</div>
            </div>
          </th>
        </tr>
      </thead>
    );
  };

  // Table component to reduce duplication
  // The row columns need ~1400px; below that the container must scroll, never clip — clipping hid
  // the Actions column (Convert / Schedule, Mark Picked Up), which has no other entry point.
  const TicketTable = ({ tickets, tab, showStatus = false }: { tickets: any[]; tab: TabValue; showStatus?: boolean }) => (
    <div className="overflow-x-auto" data-testid="optiboard-table-scroll">
      <table className="w-full">
        <TableHeader showStatus={showStatus} tab={tab} />
        <tbody>
          {tickets.map(ticket => (
            <OptiboardTicketRow 
              key={ticket.id} 
              ticket={ticket} 
              tab={ticket._tab || tab}
              technicians={technicians}
              onRefresh={refetch}
              showStatus={showStatus}
              statusLabel={ticket._tab ? getTabLabel(ticket._tab) : undefined}
            />
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopBar />
      
      <LiveUpdatesBanner state={liveState} timeZone={shopTimezone} subject="the OptiBoard" />
      <main className="flex-1 p-6">
        <div className="w-full">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">OptiBoard</h1>
              <p className="text-muted-foreground">
                Ticket lifecycle management - track tickets from estimate to pickup
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search ticket #, customer, phone, vehicle, tech..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[260px]"
                />
              </div>
            
            </div>
          </div>

          {/* Show unified search results when searching, otherwise show tabs */}
          {isSearchActive ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  Search Results for "{searchQuery}"
                </h2>
                <Badge variant="secondary">{allSearchResults.length} found</Badge>
              </div>
              
              {allSearchResults.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border rounded-lg">
                  No tickets found matching "{searchQuery}"
                </div>
              ) : (
                <TicketTable tickets={allSearchResults} tab="estimates" showStatus />
              )}
            </div>
          ) : (
            <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-4">
              <TabsList className="grid grid-cols-7 w-full overflow-hidden">
                {tabConfig.map(({ value, label, icon, color, activeClass }) => (
                  <TabsTrigger 
                    key={value} 
                    value={value} 
                    className={`flex items-center gap-2 transition-colors ${activeClass}`}
                  >
                    <span className={color}>{icon}</span>
                    <span className={`hidden sm:inline ${currentTab === value ? color : ''}`}>{label}</span>
                    <Badge variant="secondary" className="ml-1">
                      {getTabCount(value)}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="all" className="space-y-6">
                {liveMayBeTruncated && (
                  <p className="text-xs text-amber-600" data-testid="optiboard-live-may-be-truncated">
                    This shop has more live tickets than one read returns; some may not be shown.
                  </p>
                )}
                {estimates.length === 0 && scheduled.length === 0 && wip.length === 0 && completed.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No active tickets found.
                  </div>
                ) : (
                  <>
                    {/* Estimates Section */}
                    {estimates.length > 0 && (
                      <div className="space-y-3 border-l-4 border-l-blue-500 bg-blue-500/5 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <h3 className="font-semibold">Estimates</h3>
                          <Badge variant="secondary">{estimates.length}</Badge>
                        </div>
                        <TicketTable tickets={estimates} tab="estimates" />
                      </div>
                    )}
                    
                    {/* Scheduled Section */}
                    {scheduled.length > 0 && (
                      <div className="space-y-3 border-l-4 border-l-violet-500 bg-violet-500/5 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-violet-500" />
                          <h3 className="font-semibold">Scheduled</h3>
                          <Badge variant="secondary">{scheduled.length}</Badge>
                        </div>
                        <TicketTable tickets={scheduled} tab="scheduled" />
                      </div>
                    )}
                    
                    {/* WIP Section */}
                    {wip.length > 0 && (
                      <div className="space-y-3 border-l-4 border-l-amber-500 bg-amber-500/5 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <Play className="h-5 w-5 text-amber-500" />
                          <h3 className="font-semibold">Working</h3>
                          <Badge variant="secondary">{wip.length}</Badge>
                        </div>
                        <TicketTable tickets={wip} tab="wip" />
                      </div>
                    )}
                    
                    {/* Completed Section */}
                    {completed.length > 0 && (
                      <div className="space-y-3 border-l-4 border-l-green-500 bg-green-500/5 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <h3 className="font-semibold">Completed</h3>
                          <Badge variant="secondary">{completed.length}</Badge>
                        </div>
                        <TicketTable tickets={completed} tab="completed" />
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="estimates" className="space-y-2">
                <div className="space-y-3 border-l-4 border-l-blue-500 bg-blue-500/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <h3 className="font-semibold">Estimates</h3>
                    <Badge variant="secondary">{estimates.length}</Badge>
                  </div>
                  {estimates.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No estimates pending. Create one from the Create Ticket page.
                    </div>
                  ) : (
                    <TicketTable tickets={estimates} tab="estimates" />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="scheduled" className="space-y-4">
                {scheduledByDate.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    No scheduled tickets.
                  </div>
                ) : (
                  scheduledByDate.map(([dateStr, tickets]) => (
                    <div key={dateStr} className="space-y-3 border-l-4 border-l-violet-500 bg-violet-500/5 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-violet-500" />
                        <h3 className="font-semibold">
                          {dateStr === 'Unscheduled' ? 'Unscheduled' : format(parseISO(dateStr), 'EEEE, MMMM d, yyyy')}
                        </h3>
                        {dateStr !== 'Unscheduled' && isToday(parseISO(dateStr)) && (
                          <Badge className="bg-violet-500 text-white">Today</Badge>
                        )}
                        {dateStr !== 'Unscheduled' && dateStr < shopToday && (
                          <Badge variant="destructive" data-testid="optiboard-overdue-badge">Overdue</Badge>
                        )}
                        <Badge variant="secondary">{tickets.length}</Badge>
                      </div>
                      <TicketTable tickets={tickets} tab="scheduled" />
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="wip" className="space-y-2">
                <div className="space-y-3 border-l-4 border-l-amber-500 bg-amber-500/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Play className="h-5 w-5 text-amber-500" />
                    <h3 className="font-semibold">Working</h3>
                    <Badge variant="secondary">{wip.length}</Badge>
                  </div>
                  {wip.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No work in progress.
                    </div>
                  ) : (
                    <TicketTable tickets={wip} tab="wip" />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="completed" className="space-y-2">
                <div className="space-y-3 border-l-4 border-l-green-500 bg-green-500/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <h3 className="font-semibold">Completed</h3>
                    <Badge variant="secondary">{completed.length}</Badge>
                  </div>
                  {completed.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No completed tickets awaiting pickup.
                    </div>
                  ) : (
                    <TicketTable tickets={completed} tab="completed" />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="picked-up" className="space-y-2">
                <div className="space-y-3 border-l-4 border-l-teal-500 bg-teal-500/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-teal-500" />
                    <h3 className="font-semibold">Picked Up</h3>
                    <Badge variant="secondary">{pickedUp.length}{archiveTruncated.pickedUp ? '+' : ''}</Badge>
                  </div>
                  {/* Core-100 R2 CORE-AS-GAP-003: the archive tab is a recent window, and says so. */}
                  {archiveTruncated.pickedUp && (
                    <p className="text-xs text-muted-foreground" data-testid="optiboard-picked-up-window">
                      Showing the {OPTIBOARD_ARCHIVE_WINDOW} most recent. Older picked up tickets exist and are not listed here.
                    </p>
                  )}
                  {pickedUp.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No picked up tickets.
                    </div>
                  ) : (
                    <TicketTable tickets={pickedUp} tab="picked-up" />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="deleted" className="space-y-2">
                <div className="space-y-3 border-l-4 border-l-rose-500 bg-rose-500/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-rose-500" />
                    <h3 className="font-semibold">Deleted</h3>
                    <Badge variant="secondary">{deleted.length}{archiveTruncated.deleted ? '+' : ''}</Badge>
                  </div>
                  {/* Core-100 R2 CORE-AS-GAP-003: the archive tab is a recent window, and says so. */}
                  {archiveTruncated.deleted && (
                    <p className="text-xs text-muted-foreground" data-testid="optiboard-deleted-window">
                      Showing the {OPTIBOARD_ARCHIVE_WINDOW} most recent. Older deleted tickets exist and are not listed here.
                    </p>
                  )}
                  {deleted.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No deleted tickets.
                    </div>
                  ) : (
                    <TicketTable tickets={deleted} tab="deleted" />
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

export default OptiBoard;
